import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Lightbulb, CheckCircle, Clock, XCircle, Rocket, Send } from 'lucide-react';
import { useAppAuth } from '../hooks/useAppAuth';
import { createApiClient, suggestionService, type SuggestionDto } from '../services/api';

const CATEGORIES = ['Feature', 'Accessibility', 'Bug', 'Other'] as const;

const STATUS_COLOURS: Record<string, string> = {
  New:         'bg-blue-100 text-blue-700',
  UnderReview: 'bg-yellow-100 text-yellow-700',
  Planned:     'bg-purple-100 text-purple-700',
  Implemented: 'bg-green-100 text-green-700',
  Declined:    'bg-red-100 text-red-700',
};

const STATUS_ICONS: Record<string, React.ReactNode> = {
  New:         <Clock className="w-3 h-3" />,
  UnderReview: <Clock className="w-3 h-3" />,
  Planned:     <Rocket className="w-3 h-3" />,
  Implemented: <CheckCircle className="w-3 h-3" />,
  Declined:    <XCircle className="w-3 h-3" />,
};

export default function SuggestionsPage() {
  const { getToken, account } = useAppAuth();
  const client = createApiClient(getToken);
  const svc = suggestionService(client);
  const qc = useQueryClient();

  const [category, setCategory]       = useState<string>('Feature');
  const [title, setTitle]             = useState('');
  const [description, setDescription] = useState('');
  const [error, setError]             = useState<string | null>(null);
  const [submitted, setSubmitted]     = useState(false);

  const { data: mine = [], isLoading } = useQuery({
    queryKey: ['my-suggestions'],
    queryFn: () => svc.getMine(),
    enabled: !!account,
  });

  const submitMut = useMutation({
    mutationFn: () => svc.submit(category, title.trim(), description.trim()),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['my-suggestions'] });
      setTitle('');
      setDescription('');
      setSubmitted(true);
      setTimeout(() => setSubmitted(false), 4000);
    },
    onError: (e: any) => setError(e?.response?.data ?? 'Failed to submit your suggestion. Please try again.'),
  });

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!title.trim() || !description.trim()) {
      setError('Title and description are required.');
      return;
    }
    submitMut.mutate();
  };

  return (
    <div className="page-shell">
      <div className="flex items-center gap-2 mb-1">
        <Lightbulb className="w-6 h-6 text-blue-600" />
        <h1 className="text-2xl font-bold text-gray-900">Suggestions &amp; Feedback</h1>
      </div>
      <p className="text-sm text-gray-500 mb-6">
        Tell us about new features, accessibility improvements, or anything else that would make TransitDeal better.
      </p>

      <form onSubmit={onSubmit} className="bg-white border border-gray-200 rounded-xl shadow-sm p-5 mb-8 space-y-4">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Category</label>
          <div className="flex gap-2 flex-wrap">
            {CATEGORIES.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setCategory(c)}
                className={`px-3 py-1.5 text-xs font-medium rounded-full border transition-colors ${
                  category === c
                    ? 'bg-blue-600 border-blue-600 text-white'
                    : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Title</label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={150}
            placeholder="e.g. Add dark mode to the dashboard"
            className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Description</label>
          <textarea
            rows={4}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            maxLength={2000}
            placeholder="Describe your idea or the problem you're facing…"
            className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        {error && <p className="text-xs text-red-600">{error}</p>}
        {submitted && (
          <p className="text-xs text-green-700 flex items-center gap-1">
            <CheckCircle className="w-3.5 h-3.5" /> Thanks! Your suggestion has been submitted.
          </p>
        )}

        <button
          type="submit"
          disabled={submitMut.isPending}
          className="inline-flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors"
        >
          {submitMut.isPending
            ? <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            : <Send className="w-4 h-4" />}
          Submit Suggestion
        </button>
      </form>

      <h2 className="text-lg font-semibold text-gray-900 mb-3">Your Submissions</h2>
      {isLoading ? (
        <div className="flex justify-center py-10">
          <div className="animate-spin w-6 h-6 border-4 border-blue-500 border-t-transparent rounded-full" />
        </div>
      ) : mine.length === 0 ? (
        <p className="text-sm text-gray-400">You haven't submitted any suggestions yet.</p>
      ) : (
        <div className="space-y-3">
          {mine.map((s: SuggestionDto) => (
            <div key={s.suggestionId} className="bg-white border border-gray-200 rounded-xl shadow-sm p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs px-1.5 py-0.5 rounded bg-gray-100 text-gray-500">{s.category}</span>
                    <span className="font-semibold text-gray-900 text-sm truncate">{s.title}</span>
                  </div>
                  <p className="text-xs text-gray-500 line-clamp-2">{s.description}</p>
                  {s.adminNotes && (
                    <p className="text-xs text-blue-600 italic mt-1">Team note: {s.adminNotes}</p>
                  )}
                </div>
                <span className={`shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLOURS[s.status]}`}>
                  {STATUS_ICONS[s.status]}
                  {s.status}
                </span>
              </div>
              <p className="text-xs text-gray-400 mt-2">
                Submitted {new Date(s.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
